/**
 * The sharing dialog stages changes and commits them on Save, so what it holds is a desired end
 * state rather than a log of actions. This turns that end state back into the two calls the API
 * takes — grants and revocations — which is the only place the difference between them matters.
 *
 * Kept out of the component because it is the part with the edge cases: a level changed and then
 * changed back is not a change, and an organization revoked and re-added in one session is a level
 * change rather than a revoke followed by a grant.
 */
import { DataStoreAccessLevel, DataStoreShare } from "@/datastore/types";

export interface ShareChangeSet {
  grants: { organizationId: string; access: DataStoreAccessLevel }[];
  revoked: string[];
}

/**
 * @param saved the grants the server currently holds
 * @param staged the grants the dialog would leave in place
 */
export function shareChangeSet(
  saved: DataStoreShare[],
  staged: DataStoreShare[]
): ShareChangeSet {
  const savedByOrg = new Map(saved.map((s) => [s.organizationId, s.access]));
  const stagedIds = new Set(staged.map((s) => s.organizationId));

  const grants = staged
    .filter((share) => savedByOrg.get(share.organizationId) !== share.access)
    .map((share) => ({
      organizationId: share.organizationId,
      access: share.access,
    }));

  const revoked = saved
    .filter((share) => !stagedIds.has(share.organizationId))
    .map((share) => share.organizationId);

  return { grants, revoked };
}

export function hasShareChanges(
  saved: DataStoreShare[],
  staged: DataStoreShare[]
): boolean {
  const changes = shareChangeSet(saved, staged);
  return changes.grants.length > 0 || changes.revoked.length > 0;
}

/** Applies a level to every selected organization, leaving the rest untouched. */
export function setAccessFor(
  staged: DataStoreShare[],
  organizationIds: string[],
  access: DataStoreAccessLevel
): DataStoreShare[] {
  const selected = new Set(organizationIds);
  return staged.map((share) =>
    selected.has(share.organizationId) ? { ...share, access } : share
  );
}

/**
 * How many organizations the picker still has to offer: the server's total for the current search,
 * less the ones now staged in the granted panel.
 *
 * The panel itself drops a staged organization from the page it renders, but the count beside its
 * header came straight from the server's total, so it kept its pre-move value — a picker showing no
 * rows over a header that still claimed fifty. Subtracting here rather than counting the rendered
 * page is deliberate: the page is one window of a server-side search, and its length is a page
 * size, not a total.
 *
 * @param total the server's total for this search, granted organizations included
 * @param search the term that total was counted under; staged names are matched the way the
 *     backend matches them, case-insensitively and anywhere in the name
 */
export function availableTargetCount(
  total: number,
  staged: DataStoreShare[],
  search: string
): number {
  const term = search.trim().toLowerCase();
  const stagedInSearch = staged.filter(
    (share) => !term || share.name.toLowerCase().includes(term)
  ).length;
  return Math.max(0, total - stagedInSearch);
}
